import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AuthenticationEndpoints,
  ApiKeysEndpoints,
  ContainersApiEndpoints,
  DatabasesApiEndpoints,
  ProjectsApiEndpoints,
  StaticSitesApiEndpoints,
  UniversalEndpointSection,
} from '../components/ApiReference';

const sections = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    items: [
      { title: 'Quick Start Guide', slug: 'quick-start' },
      { title: 'API Discovery', slug: 'api-discovery' },
      { title: 'Authentication', slug: 'authentication' },
      { title: 'Your First Container', slug: 'first-container' },
    ],
  },
  {
    id: 'containers',
    title: '🐳 Container Platform',
    items: [
      { title: 'Overview', slug: 'containers-overview' },
      { title: 'Build & Push Images', slug: 'docker-workflow' },
      { title: 'Deploy Applications', slug: 'deploy-apps' },
      { title: 'Environment Variables', slug: 'env-vars' },
      { title: 'Container Logs', slug: 'container-logs' },
    ],
  },
  {
    id: 'databases',
    title: '📊 Databases',
    items: [
      { title: 'Database Provisioning', slug: 'db-provisioning' },
      { title: 'PostgreSQL', slug: 'postgres' },
      { title: 'MariaDB / MySQL', slug: 'mariadb' },
      { title: 'Redis Cache', slug: 'redis' },
      { title: 'MongoDB', slug: 'mongodb' },
      { title: 'Running Migrations', slug: 'db-migrations' },
    ],
  },
  {
    id: 'registry',
    title: '📦 Docker Registry',
    items: [
      { title: 'Registry Overview', slug: 'registry-overview' },
      { title: 'Push Images', slug: 'registry-push' },
      { title: 'Base Images', slug: 'base-images' },
      { title: 'Private Images', slug: 'private-images' },
    ],
  },
  {
    id: 'api',
    title: 'API Reference',
    items: [
      { title: 'Universal Endpoint', slug: 'universal-endpoint' },
      { title: 'API Keys', slug: 'api-keys' },
      { title: 'Containers API', slug: 'api-containers' },
      { title: 'Databases API', slug: 'api-databases' },
      { title: 'Projects API', slug: 'api-projects' },
      { title: 'Static Sites API', slug: 'api-static-sites' },
    ],
  },
  {
    id: 'ai-agents',
    title: '🤖 For AI Agents',
    items: [
      { title: 'AI Agent Quick Start', slug: 'ai-quick-start' },
      { title: 'Custom Tenant Slugs', slug: 'custom-slugs' },
      { title: 'Automated Deployment', slug: 'automated-deployment' },
    ],
  },
];

// API section content mapper
const apiSectionContent: Record<string, () => JSX.Element> = {
  'universal-endpoint': UniversalEndpointSection,
  'authentication': AuthenticationEndpoints,
  'api-keys': ApiKeysEndpoints,
  'api-containers': ContainersApiEndpoints,
  'api-databases': DatabasesApiEndpoints,
  'api-projects': ProjectsApiEndpoints,
  'api-static-sites': StaticSitesApiEndpoints,
};

export default function DocsPage() {
  const [activeSection, setActiveSection] = useState('getting-started');
  const [activeApiDoc, setActiveApiDoc] = useState<string | null>(null);

  const handleSubItemClick = (slug: string) => {
    // Check if this is an API reference page
    if (apiSectionContent[slug]) {
      setActiveApiDoc(slug);
    } else {
      setActiveApiDoc(null);
    }
  };

  return (
    <div className="pt-16 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              <h2 className="text-lg font-semibold text-white mb-4">Documentation</h2>
              <nav className="space-y-4">
                {sections.map((section) => (
                  <div key={section.id}>
                    <button
                      onClick={() => {
                        setActiveSection(section.id);
                        setActiveApiDoc(null);
                      }}
                      className={`text-sm font-medium mb-2 transition-colors ${
                        activeSection === section.id ? 'text-orange-400' : 'text-zinc-400 hover:text-white'
                      }`}
                    >
                      {section.title}
                    </button>
                    {activeSection === section.id && (
                      <ul className="ml-4 space-y-2 border-l border-zinc-800 pl-4">
                        {section.items.map((item) => (
                          <li key={item.slug}>
                            <button
                              onClick={() => handleSubItemClick(item.slug)}
                              className={`text-sm text-left transition-colors ${
                                activeApiDoc === item.slug
                                  ? 'text-orange-400 font-medium'
                                  : 'text-zinc-500 hover:text-orange-400'
                              }`}
                            >
                              {item.title}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <main className="lg:col-span-3">
            {/* Show API Reference if selected */}
            {activeApiDoc && apiSectionContent[activeApiDoc] ? (
              <div className="prose prose-invert max-w-none">
                {apiSectionContent[activeApiDoc]()}
              </div>
            ) : (
            <div className="prose prose-invert max-w-none">
              <h1 className="text-4xl font-bold text-white mb-4">Documentation</h1>
              <p className="text-xl text-zinc-400 mb-8">
                🐳 Container platform for modern web applications. Deploy Docker containers, provision databases, scale effortlessly.
              </p>

              {/* API-First Notice */}
              <div className="bg-gradient-to-r from-orange-500/10 to-cyan-500/10 border border-orange-500/30 rounded-2xl p-6 mb-8">
                <div className="flex items-start gap-4">
                  <span className="material-icons text-orange-400 text-3xl">auto_awesome</span>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">API-First Platform</h3>
                    <p className="text-zinc-300 mb-4">
                      WiserWebCloud is designed for programmatic access. Our universal API endpoint provides comprehensive documentation for both humans and AI agents.
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <a
                        href="https://api.wiserwebcloud.com/.well-known/wiserweb-api"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors font-medium"
                      >
                        <span className="material-icons text-sm">explore</span>
                        Universal API Endpoint
                      </a>
                      <Link
                        to="/how-it-works"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg transition-colors font-medium"
                      >
                        <span className="material-icons text-sm">description</span>
                        How It Works Guide
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Start */}
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-8 mb-8">
                <h2 className="text-2xl font-bold text-white mb-4">🚀 Quick Start (5 Minutes)</h2>
                <p className="text-zinc-400 mb-6">
                  Deploy your first containerized application with database support.
                </p>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">1. Create Account & Get API Key</h3>
                    <p className="text-zinc-400 mb-2">
                      Register at <a href="https://dashboard.wiserwebcloud.com" className="text-orange-400 hover:text-orange-300">dashboard.wiserwebcloud.com</a>
                    </p>
                    <p className="text-sm text-zinc-500 mb-2">💡 Tip: Include <code className="text-cyan-400">preferred_slug</code> to choose your tenant namespace (e.g., "mycompany")</p>
                    <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 font-mono text-sm overflow-x-auto">
                      <span className="text-purple-400">curl</span>{' '}
                      <span className="text-white">-X POST https://api.wiserwebcloud.com/api/auth/register</span>{' '}
                      <span className="text-white">-H</span>{' '}
                      <span className="text-green-400">"Content-Type: application/json"</span>{' '}
                      <span className="text-white">-d</span>{' '}
                      <span className="text-yellow-400">'{"{"}"email":"you@example.com","password":"SecurePass123!","name":"Your Name","preferred_slug":"mycompany"{"}"}'</span>
                    </div>
                    <p className="text-sm text-zinc-500 mt-2">Then create an API key in Dashboard → Settings → API Keys</p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">2. Build & Push Your Docker Image</h3>
                    <p className="text-zinc-400 mb-2">
                      Push to our free, unlimited registry at <code className="text-cyan-400">registry.wiserwebcloud.com</code>
                    </p>
                    <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 font-mono text-sm overflow-x-auto space-y-1">
                      <div>
                        <span className="text-zinc-500"># Build your image</span>
                      </div>
                      <div>
                        <span className="text-green-400">$</span>{' '}
                        <span className="text-purple-400">docker</span>{' '}
                        <span className="text-white">build -t registry.wiserwebcloud.com/yourname/myapp:v1 .</span>
                      </div>
                      <div className="mt-2">
                        <span className="text-zinc-500"># Push to our registry (no auth needed!)</span>
                      </div>
                      <div>
                        <span className="text-green-400">$</span>{' '}
                        <span className="text-purple-400">docker</span>{' '}
                        <span className="text-white">push registry.wiserwebcloud.com/yourname/myapp:v1</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">3. Create a Database (Optional)</h3>
                    <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 font-mono text-sm overflow-x-auto">
                      <span className="text-purple-400">curl</span>{' '}
                      <span className="text-white">-X POST https://api.wiserwebcloud.com/api/orgs/YOUR-SLUG/databases</span>{' '}
                      <span className="text-white">-H</span>{' '}
                      <span className="text-green-400">"X-API-Key: ww_live_YOUR_KEY"</span>{' '}
                      <span className="text-white">-d</span>{' '}
                      <span className="text-yellow-400">'{"{"}"db_type":"postgres","name":"my-app-db"{"}"}'</span>
                    </div>
                    <p className="text-sm text-zinc-500 mt-2">Supports: PostgreSQL, MariaDB, Redis, MongoDB</p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">4. Deploy Your Container</h3>
                    <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 font-mono text-sm overflow-x-auto">
                      <span className="text-purple-400">curl</span>{' '}
                      <span className="text-white">-X POST https://api.wiserwebcloud.com/api/orgs/YOUR-SLUG/apps</span>{' '}
                      <span className="text-white">-H</span>{' '}
                      <span className="text-green-400">"X-API-Key: ww_live_YOUR_KEY"</span>{' '}
                      <span className="text-white">-d</span>{' '}
                      <span className="text-yellow-400">'{"{"}"name":"My App","slug":"myapp","runtime":"rust","subdomain":"api","docker_image":"registry.wiserwebcloud.com/yourname/myapp:v1"{"}"}'</span>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">5. Your App is Live! 🎉</h3>
                    <p className="text-zinc-400">
                      Access your app at{' '}
                      <span className="text-orange-400 font-mono">https://api.mycompany.{'{unique-id}'}.io.wiserwebcloud.com</span>
                    </p>
                    <p className="text-sm text-zinc-500 mt-2">
                      💡 Alias to your custom domain in the dashboard for production use
                    </p>
                  </div>
                </div>
              </div>

              {/* Real-World Success Story */}
              <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-2xl p-6 mb-8">
                <div className="flex items-start gap-4">
                  <span className="material-icons text-green-400 text-3xl">verified</span>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-2">✅ Battle-Tested: WhatIP DynDNS Service</h3>
                    <p className="text-zinc-300 mb-4">
                      Real production deployment: Rust application with MariaDB database, deployed from scratch in under 10 minutes.
                    </p>
                    <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4 space-y-3">
                      <div className="grid md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <div className="text-zinc-500 text-xs mb-1">Tech Stack</div>
                          <div className="text-white font-mono text-xs">Rust + MariaDB</div>
                        </div>
                        <div>
                          <div className="text-zinc-500 text-xs mb-1">Container Size</div>
                          <div className="text-white font-mono text-xs">11 MB (Alpine)</div>
                        </div>
                        <div>
                          <div className="text-zinc-500 text-xs mb-1">Deploy Time</div>
                          <div className="text-white font-mono text-xs">&lt; 10 minutes</div>
                        </div>
                      </div>
                      <div className="border-t border-zinc-800 pt-3">
                        <p className="text-xs text-zinc-400 mb-2">Deployment flow:</p>
                        <ol className="text-xs text-zinc-300 space-y-1 ml-4">
                          <li>1. Built Rust binary with static linking (musl)</li>
                          <li>2. Created multi-stage Dockerfile (11 MB final image)</li>
                          <li>3. Pushed to registry.wiserwebcloud.com/whatip/dyndns:v3</li>
                          <li>4. Provisioned MariaDB database via API</li>
                          <li>5. Created app container with <code className="text-cyan-400">docker_image</code> field</li>
                          <li>6. Platform auto-injected DATABASE_URL and PORT</li>
                          <li>7. App ran migrations on startup (sqlx::migrate!)</li>
                          <li>8. Live at https://dyndns.whatip.{'{'}unique-id{'}'}.io.wiserwebcloud.com</li>
                        </ol>
                      </div>
                      <div className="border-t border-zinc-800 pt-3">
                        <span className="inline-flex items-center gap-2 text-green-400 text-sm font-medium">
                          <span className="material-icons text-sm">verified</span>
                          Production verified deployment
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Common Gotchas & Pro Tips */}
              <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/30 rounded-2xl p-6 mb-8">
                <div className="flex items-start gap-4">
                  <span className="material-icons text-yellow-400 text-3xl">lightbulb</span>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-4">⚡ Common Gotchas & Pro Tips</h3>
                    <p className="text-zinc-300 mb-4">Lessons learned from real deployments. Read these to deploy successfully on your first attempt!</p>
                    
                    <div className="space-y-4">
                      <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
                        <h4 className="font-semibold text-orange-400 mb-2 flex items-center gap-2">
                          <span className="material-icons text-sm">warning</span>
                          Registry is REQUIRED - No Docker Hub/GHCR
                        </h4>
                        <p className="text-sm text-zinc-400 mb-2">
                          We <strong className="text-white">ONLY</strong> pull from <code className="text-cyan-400">registry.wiserwebcloud.com</code> due to Docker Hub licensing restrictions.
                        </p>
                        <div className="bg-zinc-950 rounded p-2 font-mono text-xs text-zinc-300">
                          ✅ registry.wiserwebcloud.com/yourname/app:v1<br/>
                          ❌ docker.io/yourname/app:v1<br/>
                          ❌ ghcr.io/yourname/app:v1
                        </div>
                      </div>

                      <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
                        <h4 className="font-semibold text-cyan-400 mb-2 flex items-center gap-2">
                          <span className="material-icons text-sm">storage</span>
                          Database Migrations = Your App's Job
                        </h4>
                        <p className="text-sm text-zinc-400 mb-2">
                          You get an <strong className="text-white">EMPTY database</strong>. Your app must create tables/schema on startup using:
                        </p>
                        <ul className="text-xs text-zinc-400 space-y-1 ml-4">
                          <li>• Rust: <code className="text-cyan-400">sqlx::migrate!()</code></li>
                          <li>• Node.js: <code className="text-cyan-400">prisma migrate deploy</code></li>
                          <li>• Python: <code className="text-cyan-400">alembic upgrade head</code></li>
                          <li>• Go: <code className="text-cyan-400">golang-migrate</code></li>
                        </ul>
                        <p className="text-xs text-yellow-300 mt-2">
                          🚫 No SSH, no psql/mariadb-client access. This is standard for container platforms (Heroku, Railway, Fly.io).
                        </p>
                      </div>

                      <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
                        <h4 className="font-semibold text-purple-400 mb-2 flex items-center gap-2">
                          <span className="material-icons text-sm">link</span>
                          Database Hostname: Use Container Name
                        </h4>
                        <p className="text-sm text-zinc-400 mb-2">
                          Databases use Docker container hostnames, <strong className="text-white">NOT</strong> localhost or IP addresses:
                        </p>
                        <div className="bg-zinc-950 rounded p-2 font-mono text-xs text-zinc-300">
                          ✅ wiserweb-mariadb-shared:3306<br/>
                          ✅ wiserweb-postgres-shared:5432<br/>
                          ❌ localhost:3306<br/>
                          ❌ 127.0.0.1:5432
                        </div>
                        <p className="text-xs text-green-300 mt-2">
                          💡 We auto-inject <code className="text-cyan-400">DATABASE_URL</code> with the correct hostname when you link a database!
                        </p>
                      </div>

                      <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
                        <h4 className="font-semibold text-green-400 mb-2 flex items-center gap-2">
                          <span className="material-icons text-sm">description</span>
                          Read Container Logs for Debugging
                        </h4>
                        <p className="text-sm text-zinc-400 mb-2">
                          If your app won't start, check the logs via API:
                        </p>
                        <div className="bg-zinc-950 rounded p-2 font-mono text-xs text-zinc-300 overflow-x-auto">
                          GET /api/orgs/YOUR-SLUG/apps/{'{'}app_id{'}'}/logs
                        </div>
                        <p className="text-xs text-zinc-400 mt-2">
                          Common issues: missing env vars, wrong PORT binding, database connection failures.
                        </p>
                      </div>

                      <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
                        <h4 className="font-semibold text-orange-400 mb-2 flex items-center gap-2">
                          <span className="material-icons text-sm">settings_ethernet</span>
                          Listen on 0.0.0.0 (Not 127.0.0.1)
                        </h4>
                        <p className="text-sm text-zinc-400 mb-2">
                          Your app <strong className="text-white">MUST</strong> bind to <code className="text-cyan-400">0.0.0.0:{'{'}PORT{'}'}</code> to receive external traffic:
                        </p>
                        <div className="bg-zinc-950 rounded p-2 font-mono text-xs text-zinc-300">
                          ✅ 0.0.0.0:8080 (accepts external connections)<br/>
                          ❌ 127.0.0.1:8080 (localhost only, won't work)
                        </div>
                        <p className="text-xs text-green-300 mt-2">
                          💡 We inject <code className="text-cyan-400">PORT</code> env var (usually 8080). Use it!
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Feature Cards */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="p-6 bg-zinc-900/50 border border-zinc-800 rounded-xl hover:border-orange-500/50 transition-colors">
                  <span className="material-icons text-orange-400 text-3xl mb-3">rocket_launch</span>
                  <h3 className="text-lg font-semibold text-white mb-2">Container Deployments</h3>
                  <p className="text-sm text-zinc-400 mb-4">Deploy any Dockerized app. Rust, Node.js, Python, Go - we handle them all.</p>
                  <Link to="/docs/containers-overview" className="text-orange-400 hover:text-orange-300 text-sm font-medium inline-flex items-center gap-1">
                    Learn more <span className="material-icons text-sm">arrow_forward</span>
                  </Link>
                </div>

                <div className="p-6 bg-zinc-900/50 border border-zinc-800 rounded-xl hover:border-cyan-500/50 transition-colors">
                  <span className="material-icons text-cyan-400 text-3xl mb-3">storage</span>
                  <h3 className="text-lg font-semibold text-white mb-2">Multi-Engine Databases</h3>
                  <p className="text-sm text-zinc-400 mb-4">PostgreSQL, MariaDB, Redis, MongoDB - provision in seconds via API.</p>
                  <Link to="/docs/db-provisioning" className="text-cyan-400 hover:text-cyan-300 text-sm font-medium inline-flex items-center gap-1">
                    Learn more <span className="material-icons text-sm">arrow_forward</span>
                  </Link>
                </div>

                <div className="p-6 bg-zinc-900/50 border border-zinc-800 rounded-xl hover:border-purple-500/50 transition-colors">
                  <span className="material-icons text-purple-400 text-3xl mb-3">api</span>
                  <h3 className="text-lg font-semibold text-white mb-2">Universal API Endpoint</h3>
                  <p className="text-sm text-zinc-400 mb-4">Self-documenting API designed for both humans and AI agents.</p>
                  <a 
                    href="https://api.wiserwebcloud.com/.well-known/wiserweb-api" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-purple-400 hover:text-purple-300 text-sm font-medium inline-flex items-center gap-1"
                  >
                    Explore API <span className="material-icons text-sm">open_in_new</span>
                  </a>
                </div>

                <div className="p-6 bg-zinc-900/50 border border-zinc-800 rounded-xl hover:border-green-500/50 transition-colors">
                  <span className="material-icons text-green-400 text-3xl mb-3">smart_toy</span>
                  <h3 className="text-lg font-semibold text-white mb-2">AI Agent Ready</h3>
                  <p className="text-sm text-zinc-400 mb-4">Built for autonomous deployment by AI coding assistants.</p>
                  <Link to="/docs/ai-quick-start" className="text-green-400 hover:text-green-300 text-sm font-medium inline-flex items-center gap-1">
                    Learn more <span className="material-icons text-sm">arrow_forward</span>
                  </Link>
                </div>
              </div>

              {/* Important Notes */}
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 space-y-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <span className="material-icons text-yellow-400">info</span>
                  Platform Architecture & Limitations
                </h3>
                
                <div className="space-y-3 text-sm">
                  <div className="flex gap-3">
                    <span className="material-icons text-orange-400 text-sm mt-0.5">check_circle</span>
                    <div className="flex-1">
                      <p className="text-zinc-400">
                        <strong className="text-white">Container-First Platform:</strong> This is a containerized platform (like Heroku, Railway, Fly.io). No SSH/SFTP access. Deploy via API or Docker push only.
                      </p>
                      <p className="text-xs text-zinc-500 mt-1">
                        Your workflow: Build Docker image → Push to registry.wiserwebcloud.com → Deploy via API
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <span className="material-icons text-cyan-400 text-sm mt-0.5">check_circle</span>
                    <div className="flex-1">
                      <p className="text-zinc-400">
                        <strong className="text-white">Database Migrations in App Code:</strong> Provisioned databases are EMPTY. Your app must create tables/schema on startup using migration tools.
                      </p>
                      <p className="text-xs text-zinc-500 mt-1">
                        No direct DB access (no SSH, no mariadb-client). Use sqlx::migrate!(), Prisma, Alembic, etc.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <span className="material-icons text-purple-400 text-sm mt-0.5">check_circle</span>
                    <div className="flex-1">
                      <p className="text-zinc-400">
                        <strong className="text-white">Self-Hosted Registry ONLY:</strong> We ONLY pull from registry.wiserwebcloud.com (not Docker Hub/GHCR) due to licensing restrictions.
                      </p>
                      <p className="text-xs text-zinc-500 mt-1">
                        Free, unlimited, HTTPS, no auth needed. Base images: base/rust:alpine, base/node:20-alpine, base/python:3.12-alpine
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <span className="material-icons text-green-400 text-sm mt-0.5">check_circle</span>
                    <div className="flex-1">
                      <p className="text-zinc-400">
                        <strong className="text-white">Auto-Injected Environment Variables:</strong> When you link a database, we automatically inject connection details into your container.
                      </p>
                      <p className="text-xs text-zinc-500 mt-1">
                        Available vars: DATABASE_URL, DATABASE_HOST, DATABASE_PORT, DATABASE_USER, DATABASE_PASSWORD, DATABASE_NAME, PORT (8080)
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <span className="material-icons text-yellow-400 text-sm mt-0.5">check_circle</span>
                    <div className="flex-1">
                      <p className="text-zinc-400">
                        <strong className="text-white">Shared Database Tier:</strong> Databases run in shared containers (one MariaDB container hosts multiple tenant databases). Cost-effective and efficient.
                      </p>
                      <p className="text-xs text-zinc-500 mt-1">
                        Database isolation via separate users/schemas. Dedicated containers available on enterprise plans.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <span className="material-icons text-blue-400 text-sm mt-0.5">check_circle</span>
                    <div className="flex-1">
                      <p className="text-zinc-400">
                        <strong className="text-white">Docker Networking:</strong> Apps and databases communicate via Docker network using container hostnames (not localhost).
                      </p>
                      <p className="text-xs text-zinc-500 mt-1">
                        Example: wiserweb-mariadb-shared:3306, wiserweb-postgres-shared:5432 (handled automatically in DATABASE_URL)
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Next Steps */}
              <div className="mt-8 bg-gradient-to-r from-orange-500/10 to-purple-500/10 border border-orange-500/30 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">📚 Next Steps</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <Link to="/docs/containers-overview" className="flex items-center gap-3 text-zinc-300 hover:text-orange-400 transition-colors">
                    <span className="material-icons">arrow_forward</span>
                    <span>Learn about container deployments</span>
                  </Link>
                  <Link to="/docs/db-provisioning" className="flex items-center gap-3 text-zinc-300 hover:text-cyan-400 transition-colors">
                    <span className="material-icons">arrow_forward</span>
                    <span>Provision your first database</span>
                  </Link>
                  <Link to="/docs/db-migrations" className="flex items-center gap-3 text-zinc-300 hover:text-purple-400 transition-colors">
                    <span className="material-icons">arrow_forward</span>
                    <span>Understand database migrations</span>
                  </Link>
                  <a href="https://dashboard.wiserwebcloud.com" className="flex items-center gap-3 text-zinc-300 hover:text-green-400 transition-colors">
                    <span className="material-icons">arrow_forward</span>
                    <span>Open the dashboard</span>
                  </a>
                </div>
              </div>
            </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
